// Indented sub-form for nested `z.object({...})` schemas. Recurses each
// child via the FieldRenderer dispatch at path `${parent}.${child}`.
// Section chrome (indent + heading) renders only when label is non-
// empty — the walker emits `label = ''` for array-element nodes so
// list items don't add a redundant heading + indent.

import type { ArrayOverrides } from '../form'
import type { ObjectNode } from '../schema-to-fields'
import { FieldRenderer } from '../field-renderer'

export interface NestedFieldProps {
  readonly node: ObjectNode
  readonly value: unknown
  readonly path: string
  readonly idPrefix: string
  readonly errors: Readonly<Record<string, string>>
  readonly touched: ReadonlySet<string>
  readonly markTouched: (path: string) => void
  readonly onChange: (next: unknown) => void
  readonly arrayOverrides?: ArrayOverrides
  readonly blockId?: string
}

export function NestedField({
  node,
  value,
  path,
  idPrefix,
  errors,
  touched,
  markTouched,
  onChange,
  arrayOverrides,
  blockId,
}: NestedFieldProps) {
  const obj =
    typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {}
  const grid = (
    <div class="flex flex-col gap-3">
      {node.children.map((child) => (
        <FieldRenderer
          key={child.name}
          node={child}
          value={obj[child.name]}
          path={`${path}.${child.name}`}
          idPrefix={idPrefix}
          errors={errors}
          touched={touched}
          markTouched={markTouched}
          onChange={(next) => onChange({ ...obj, [child.name]: next })}
          {...(arrayOverrides !== undefined ? { arrayOverrides } : {})}
          {...(blockId !== undefined ? { blockId } : {})}
        />
      ))}
    </div>
  )

  if (node.label === '') return grid

  return (
    <div class="flex flex-col gap-2 border-l-2 border-gray-200 pl-4">
      <h3 class="text-sm font-semibold text-gray-700">{node.label}</h3>
      {grid}
    </div>
  )
}
